// composables/useApiFetchObject.ts
import { getApiFetchConfig, handlePending, handleError } from './useApiFetchBase'
import { refreshAuthToken } from './../auth/useAuthRefresh'
import type { UseFetchOptions } from '#app'
export const useApiFetchObject = async <T>(
  url: string,
  options: UseFetchOptions<T, unknown> = {},
  api: 'api1' | 'api2' = 'api1'
) => {
  type ApiResponse<T> = {
    success: boolean;
    message: string;
    data: T;
  };

  const router = useRouter()
  const route = useRoute()
  const refreshToken = useCookie<string | null>('iqa-refresh-token')
  const tokenExp = useCookie<number | null>('iqa-token-exp') || ref(0)
  const isLogin = (import.meta.client && 
    (route.path !== '/login' && 
      route.path !== '/selectTenant' && 
      route.path !== '/customers' &&
      route.path !== '/resetPassword')) ? false : true;
  // PRE-CHECK antes de cualquier request
  
  if(!isLogin && (!refreshToken.value || !tokenExp.value))
  {
    useAuthStore().logout()
    await nextTick()
    await redirectToLoginIfNeeded();
    
    return createFetchLikeResponse({
      success: false,
      message: 'Unauthorized',
      data: {} as T
    })
  }

  const NOW = Date.now()
  const SAFE_MARGIN = 2 * 60 * 1000
  if (!isLogin && (NOW + SAFE_MARGIN >= (tokenExp.value ?? 0)))
  {
    try {
      await refreshAuthToken();
    } catch (e) {
      // si falla el refresh → logout directo

      useAuthStore().logout()
      await nextTick()
      await redirectToLoginIfNeeded();
      
      return createFetchLikeResponse({
        success: false,
        message: 'Unauthorized',
        data: {} as T
      })
    }
  }

  const executeFetch = async () => {
    const { baseURL, headers } = getApiFetchConfig(options, api);
    return await useFetch<ApiResponse<T>>(`${baseURL}${url}`, {
      ...(options as Record<string, any>),
      headers,
      key: `${url}-${Date.now()}` // evita cache raro en retry
    });
  };

  try {
    let { data, error, status, pending } = await executeFetch();

    handlePending(pending);
    
    const cause = error.value?.cause as { message?: string } | undefined;
    if (cause?.message?.includes('Failed to fetch'))
    {
      handleFatalError("Hubo un problema al intentar conectarse al servidor. Por favor, revise su conexión."); //No usar await
      redirectToLoginIfNeeded(); //No usar await
      //No usar await, porque en caso de que a nivel de componente se levanta un modal, bloquea la acción de dar click del sweet alert,
      //porque el focus del modal bloquea el sweet alert, A PESAR, de que el sweet alert se muestre encima del modal.
    }

    if (error.value)
    {
      const errorStatus = error.value.statusCode ?? error.value.status ?? error.value.status;

      if (errorStatus === 401)
      {
        useAuthStore().logout();
        await nextTick();
        await redirectToLoginIfNeeded();
        
        return createFetchLikeResponse({
          success: false,
          message: 'Unauthorized',
          data: {} as T
        })
      }
        
      const fallback = handleError<T>(error.value, url);
      //fallback.data = {} as T;
      data.value = fallback;
    }
    return { data, error, status, pending };
  }
  finally {
    swal_close();
  }
};

function createFetchLikeResponse<T>(payload: {
  success: boolean
  message: string
  data: T
}) {
  return {
    data: ref(payload),
    error: ref(null),
    status: ref(401),
    pending: ref(false)
  }
}